import { useField } from 'formik';
import { useMemo, useState, useEffect, SyntheticEvent } from 'react';
import { Chip, TextField, InputLabel, Autocomplete } from '@mui/material';

import { controlError } from 'utils/error';
import { questionLabelText } from 'utils/question-label';
import { QuestionLabel } from 'components/question-label';
import { CsvLookupFormField, CsvLookupStoredValue } from 'types';
import { FormReferenceItem, useLazyReferenceItemsQuery } from 'store/api/forms';

const SEARCH_DEBOUNCE_MS = 300;
const MIN_SEARCH_QUERY_LENGTH = 2;
const SEARCH_RESULT_LIMIT = 20;

type Option = {
  id: string;
  searchValue: string;
  displayValue: string;
  referenceItemId: string;
};

const optionFromStored = (entry: CsvLookupStoredValue): Option => ({
  id: entry.referenceItemId,
  searchValue: entry.searchValue,
  displayValue: entry.displayValue,
  referenceItemId: entry.referenceItemId,
});

const optionFromApi = (entry: FormReferenceItem): Option => ({
  id: entry.id,
  referenceItemId: entry.id,
  searchValue: entry.searchValue,
  displayValue: entry.displayValue,
});

export const FormCsvLookupField = ({
  id,
  label,
  options,
  placeholder,
}: CsvLookupFormField) => {
  const [field, meta, helpers] =
    useField<Array<Option | CsvLookupStoredValue>>(id);
  const [inputValue, setInputValue] = useState('');
  const selected = useMemo(
    () =>
      Array.isArray(field.value)
        ? field.value.map((entry) =>
            'id' in entry ? entry : optionFromStored(entry)
          )
        : [],
    [field.value]
  );
  const [
    searchReferenceItems,
    { isError, isFetching, currentData, originalArgs },
  ] = useLazyReferenceItemsQuery();
  // The saved form version that owns the current dataset. Field IDs remain
  // stable across form versions when CSV data is reused.
  const formId = options.itemsFormId;
  const query = inputValue.trim();
  const hasSearchQuery = query.length >= MIN_SEARCH_QUERY_LENGTH;
  const isCurrentQuery =
    originalArgs?.formId === formId &&
    originalArgs?.fieldId === id &&
    originalArgs?.search === query;
  const results = isCurrentQuery ? (currentData ?? []).map(optionFromApi) : [];
  const loading =
    Boolean(formId) && hasSearchQuery && (!isCurrentQuery || isFetching);
  const searchError =
    isCurrentQuery && isError ? 'Unable to load matching records' : undefined;
  const errorText = controlError(meta, id, label);

  useEffect(() => {
    if (!formId || !hasSearchQuery) {
      return undefined;
    }

    let request: ReturnType<typeof searchReferenceItems> | undefined;
    const timeout = window.setTimeout(() => {
      request = searchReferenceItems({
        formId,
        fieldId: id,
        search: query,
        limit: SEARCH_RESULT_LIMIT,
      });
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timeout);
      request?.abort();
    };
  }, [formId, hasSearchQuery, id, query, searchReferenceItems]);

  const handleSelectionChange = (_: SyntheticEvent, value: Option[]) => {
    setInputValue('');
    helpers.setValue(value);
  };

  const removeSelected = (idToRemove: string) => {
    helpers.setValue(selected.filter((option) => option.id !== idToRemove));
  };

  return (
    <div>
      <InputLabel component="div" className="mb-2">
        <QuestionLabel value={label} />
      </InputLabel>
      <Autocomplete
        multiple
        value={selected}
        loading={loading}
        filterSelectedOptions
        inputValue={inputValue}
        renderValue={() => null}
        onChange={handleSelectionChange}
        filterOptions={(items) => items}
        options={hasSearchQuery ? results : []}
        getOptionLabel={(option) => option.displayValue}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        onInputChange={(_, value) => {
          setInputValue(value);
        }}
        noOptionsText={
          hasSearchQuery ? 'No options' : 'Type at least 2 characters to search'
        }
        sx={{
          '& .MuiOutlinedInput-root': {
            minHeight: 56,
          },
          '& .MuiAutocomplete-endAdornment': {
            top: '50%',
            transform: 'translateY(-50%)',
          },
        }}
        renderOption={(props, option) => {
          const { key, ...optionProps } = props;

          return (
            <li key={key} {...optionProps}>
              <span style={{ whiteSpace: 'pre-line' }}>
                {option.searchValue}
              </span>
            </li>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            name={id}
            onBlur={field.onBlur}
            placeholder={placeholder}
            error={Boolean(errorText) || Boolean(searchError)}
            slotProps={{
              ...params.slotProps,
              htmlInput: {
                ...params.slotProps.htmlInput,
                'aria-label': questionLabelText(label),
              },
            }}
            helperText={
              errorText
                ? errorText
                : !formId
                  ? 'Save the form before previewing CSV lookup results.'
                  : searchError
            }
          />
        )}
      />

      {selected.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-2">
          {selected.map((option) => (
            <Chip
              key={option.id}
              label={option.displayValue}
              onDelete={() => removeSelected(option.id)}
              sx={{
                maxWidth: '100%',
                '& .MuiChip-label': {
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                },
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
};
